import { FC, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Checkbox, Chip, FormControlLabel, Skeleton, Stack, TextField, Typography } from '@mui/material'

import { isFetchBaseQueryError } from '../../../helpers/isFetchBaseQueryError'
import utilsService from '../../../services/UtilsService'
import {
  useAddInternalOrganizationUserMutation,
  useGetInternalUserSettingsQuery,
  useLazyGetInternalOrganizationUsersQuery,
  useUpdateInternalOrganizationUserMutation,
  useUpdateInternalUserSettingsMutation,
} from '../../api/organizations'
import { getOrganizationRoles } from '../../features/organizations/components/helpers/organizationUserHelpers'
import { InternalOrganization, InternalUser, OrganizationUserCreate } from '../../features/organizations/types/Organizations'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalFormDialog } from '../InternalFormDialog/InternalFormDialog'
import {
  EditUserFormValues,
  generatePassword,
  getEmailError,
  getPasswordError,
  getUnstoredUserFields,
  isEditUserFormChanged,
  isEditUserFormValid,
  toEditUserFormValues,
  toUserCreate,
  toUserUpdate,
  userFieldLabelKeys,
} from './editUserHelpers'

type EditUserDialogProps = {
  open: boolean
  organization: InternalOrganization
  /** Leave out to add a user to the organization. */
  user?: InternalUser
  onClose: () => void
  onSaved?: (stored: InternalUser) => void
}

const newPassword = () => generatePassword(() => utilsService.getRandomPassword())

/** Adds a user to an organization or edits one; shared by Organizations and Users. Every save is read back. */
export const EditUserDialog: FC<EditUserDialogProps> = ({ open, organization, user, onClose, onSaved }) => {
  const { t } = useTranslation()
  const { notify, run } = useMutationFeedback()
  // A user added by a save whose read-back differed; the dialog then edits it, so saving again doesn't add another.
  const [createdUser, setCreatedUser] = useState<InternalUser>()
  const editedUser = user ?? createdUser
  const isNew = !editedUser
  const [initialValues, setInitialValues] = useState(() => toEditUserFormValues(user, newPassword()))
  const { control, handleSubmit, reset, watch } = useForm<EditUserFormValues>({ mode: 'onTouched', defaultValues: initialValues })
  const [addUser, { isLoading: isAdding }] = useAddInternalOrganizationUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateInternalOrganizationUserMutation()
  const [readOrganizationUsers] = useLazyGetInternalOrganizationUsersQuery()
  const settingsQuery = useGetInternalUserSettingsQuery(editedUser?.username ?? '', { skip: !open || !editedUser })
  const [updateSettings, { isLoading: isUpdatingSettings }] = useUpdateInternalUserSettingsMutation()

  useEffect(() => {
    if (!open) return
    const values = toEditUserFormValues(user, newPassword())
    setInitialValues(values)
    setCreatedUser(undefined)
    reset(values)
  }, [open, user, reset])

  const roles = useMemo(() => getOrganizationRoles(organization), [organization])
  const values = watch()
  const isSaving = isAdding || isUpdating
  const canSave = isEditUserFormValid(values, isNew) && isEditUserFormChanged(values, initialValues) && !isSaving
  const emailChanged = !isNew && values.email.trim() !== editedUser.email

  const title = isNew
    ? t('internal-csm:edit_user_add_title', { organization: organization.name })
    : editedUser.firstName && editedUser.lastName
    ? t('internal-csm:edit_user_edit_title', { name: `${editedUser.firstName} ${editedUser.lastName}`, organization: organization.name })
    : t('internal-csm:edit_user_edit_title_no_name', { organization: organization.name })

  // A failed add may still have created the user (e.g. only its read-back or admin call failed); adopting it makes a retry update it.
  const adoptUserAddedDespiteFailure = async (email: string) => {
    try {
      const users = await readOrganizationUsers(organization.id).unwrap()
      const added = users.find((stored) => stored.email.toLowerCase() === email.toLowerCase())
      if (added) setCreatedUser(added)
    } catch {}
  }

  const save = async (formValues: EditUserFormValues) => {
    try {
      let sent: InternalUser | OrganizationUserCreate
      let stored: InternalUser | undefined
      if (isNew) {
        sent = toUserCreate(organization.id, formValues)
        stored = (await addUser({ user: sent, sendInvite: formValues.sendInvite, admin: formValues.oadmin }).unwrap()).stored
        if (stored) setCreatedUser(stored)
      } else {
        sent = toUserUpdate(editedUser, formValues)
        const admin = formValues.oadmin !== editedUser.oadmin ? formValues.oadmin : undefined
        stored = (await updateUser({ organizationId: organization.id, user: sent, admin }).unwrap()).stored
        if (stored && createdUser) setCreatedUser(stored)
      }
      if (!stored) {
        notify(t('internal-csm:edit_user_not_found_after_save', { email: sent.email }), 'error')
        return
      }
      const unstored = getUnstoredUserFields(sent, stored, formValues.oadmin)
      if (unstored.length) {
        notify(t('internal-csm:edit_user_not_stored', { fields: unstored.map((field) => t(userFieldLabelKeys[field])).join(', ') }), 'error')
        return
      }
      notify(t(user ? 'internal-csm:edit_user_updated' : 'internal-csm:edit_user_added', { email: stored.email }), 'success')
      onSaved?.(stored)
      onClose()
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 409) {
        notify(t('internal-csm:edit_user_duplicate_email'), 'error')
        return
      }
      // A 409 means the e-mail was already taken, so a user found by it wasn't added by this save.
      if (isNew) await adoptUserAddedDespiteFailure(formValues.email.trim())
      const reason = getValidationErrorMessage(error)
      notify(reason ? `${t('internal-csm:edit_user_save_failed')}: ${reason}` : t('internal-csm:edit_user_save_failed'), 'error')
    }
  }

  const dailyDigest = settingsQuery.data?.daily_digest ?? false
  const toggleDailyDigest = async () => {
    if (!editedUser) return
    const { username } = editedUser
    const next = !dailyDigest
    const saved = await run(updateSettings({ username, settings: { daily_digest: next } }).unwrap(), {
      error: t('internal-csm:edit_user_daily_digest_failed', { username }),
    })
    if (!saved.ok) return
    if (saved.result.stored?.daily_digest !== next) {
      notify(t('internal-csm:edit_user_daily_digest_not_stored', { username }), 'error')
      return
    }
    notify(t('internal-csm:edit_user_daily_digest_saved', { username }), 'success')
  }

  const saveLabel = isNew ? (values.sendInvite ? 'internal-csm:edit_user_send_invite' : 'internal-csm:edit_user_add') : 'internal-common:save'

  return (
    <InternalFormDialog open={open} title={title} onClose={onClose} onSave={handleSubmit(save)} saveDisabled={!canSave} saveLabel={t(saveLabel)}>
      <Stack spacing={2} pt={1}>
        <Controller
          name="email"
          control={control}
          rules={{ validate: (value) => (getEmailError(value) ? t(`internal-csm:edit_user_${getEmailError(value)}`) : true) }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="email"
              size="small"
              fullWidth
              required
              label={t('internal-csm:edit_user_email')}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ?? (emailChanged ? t('internal-csm:edit_user_username_stays', { username: editedUser?.username }) : undefined)
              }
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => <TextField {...field} size="small" fullWidth label={t('internal-csm:edit_user_first_name')} />}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => <TextField {...field} size="small" fullWidth label={t('internal-csm:edit_user_last_name')} />}
          />
        </Stack>
        <Controller
          name="title"
          control={control}
          render={({ field }) => <TextField {...field} size="small" fullWidth label={t('internal-csm:edit_user_title')} />}
        />
        <Controller
          name="oadmin"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('internal-csm:edit_user_admin')}
            />
          )}
        />
        {!isNew && (
          <FormControlLabel
            control={
              settingsQuery.isLoading ? (
                <Skeleton variant="rectangular" width={18} height={18} sx={{ m: 1.5 }} />
              ) : (
                <Checkbox checked={dailyDigest} disabled={settingsQuery.isError || isUpdatingSettings} onChange={toggleDailyDigest} />
              )
            }
            label={settingsQuery.isError ? t('internal-csm:edit_user_daily_digest_load_failed') : t('internal-csm:edit_user_daily_digest')}
          />
        )}
        {isNew && (
          <Controller
            name="sendInvite"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label={t('internal-csm:edit_user_send_invite_label')}
              />
            )}
          />
        )}
        {isNew && !values.sendInvite && (
          <Controller
            name="password"
            control={control}
            rules={{
              validate: (value) => {
                const error = getPasswordError({ password: value, sendInvite: false }, true)
                return error ? t(`internal-csm:edit_user_${error}`) : true
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                fullWidth
                required
                label={t('internal-csm:edit_user_password')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? t('internal-csm:edit_user_password_help')}
              />
            )}
          />
        )}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t('internal-csm:edit_user_roles')}
          </Typography>
          {roles.length ? (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {roles.map((role) => (
                <Chip key={role} label={role} size="small" />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('internal-csm:edit_user_no_roles')}
            </Typography>
          )}
        </Box>
      </Stack>
    </InternalFormDialog>
  )
}
